import { Box, styled } from '@mui/material';
import { ExpandMore } from '@mui/icons-material';
import React, { forwardRef, ReactElement } from 'react';
import { useUID } from 'react-uid';
import { colorDarkGrey, colorGrey } from 'customColors';

export interface SelectProps
  extends Omit<JSX.IntrinsicElements['select'], 'ref'> {
  name?: string;
  label?: string;
  hasError?: boolean;
  placeholder?: string;
  className?: string;
  children?:
    | JSX.IntrinsicElements['option']
    | JSX.IntrinsicElements['option'][];
}

export const Select = styled(
  forwardRef(
    (
      {
        name,
        label,
        hasError = false,
        className,
        children,
        placeholder,
        ...selectProps
      }: SelectProps,
      ref?: React.Ref<HTMLSelectElement>,
    ): ReactElement => {
      const id = name ? `${name}-${useUID()}` : useUID();

      return (
        <>
          <Box
            display="flex"
            flexDirection="column"
            className={className}
            sx={{
              '& .label': {
                fontSize: 14,
                fontWeight: 500,
                color: theme =>
                  hasError
                    ? theme.palette.error.main
                    : theme.palette.text.primary,
                cursor: 'pointer',
                WebkitTapHighlightColor: 'rgba(0,0,0,0)',
                whiteSpace: 'nowrap',
              },
              '& .input': {
                width: '100%',
                background: theme => theme.palette.neutral.main,
                border: '1px solid',
                borderRadius: 4,
                fontSize: '1em',
                outline: 'none',
                appearance: 'none',
                borderColor: theme =>
                  hasError
                    ? theme.palette.error.light
                    : theme.palette.neutral.dark,
                paddingLeft: 10,
                paddingRight: 40,
                height: 50,
                '&::placeholder': {
                  color: theme => theme.palette.neutral.darker,
                },
                '&[value=""]': {
                  color: theme => theme.palette.neutral.light,
                },
                '&:focus': {
                  borderColor: theme => theme.palette.primary.main,
                  boxShadow: theme => `0 0 0 1px ${theme.palette.primary.main}`,
                },
                '&:focus + *': {
                  borderColor: theme => theme.palette.primary.main,
                  borderWidth: 2,
                },
              },
            }}
          >
            {label && (
              <label htmlFor={id} className="label">
                {label}
              </label>
            )}
            <Box position="relative">
              <Box
                position="absolute"
                bottom={10}
                right={10}
                lineHeight={0}
                style={{ pointerEvents: 'none' }}
              >
                <ExpandMore color="primary" />
              </Box>
              <select
                name={name}
                id={id}
                className="input"
                ref={ref}
                style={{
                  color: selectProps.value ? colorDarkGrey : colorGrey,
                }}
                aria-invalid={hasError}
                {...selectProps}
              >
                {placeholder && (
                  <option value="" disabled hidden style={{ color: 'grey' }}>
                    {placeholder}
                  </option>
                )}
                {children}
              </select>
            </Box>
          </Box>
        </>
      );
    },
  ),
)``;
